import { useState } from 'react';
import axios from 'axios';
import { MapPin, Loader2, Send, Anchor } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISLANDS = ['Maui', 'Oahu', 'Big Island', 'Kauai', 'Lagos', 'Abuja', 'Open to Sovereign Counsel'];
const BUDGETS = ['$250K - $750K', '$750K - $2M', '$2M - $5M', '$5M+'];
const INTENTS = ['Primary Residence', 'Investment / Rental', 'Legacy Asset', 'Sound Studio + Compound'];
const TIMELINES = ['Immediate (0-3 mo)', 'Soon (3-9 mo)', 'Within a Year', 'Strategic / Watching'];

export default function IslandBaseLead({ compact = false }) {
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '',
    target_island: 'Maui', budget_band: '$750K - $2M',
    intent: 'Primary Residence', timeline: 'Soon (3-9 mo)',
    message: '', referral_source: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/rescue/island-lead`, form);
      toast.success(data.message);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signal failed to send. Try again.');
    }
    setSubmitting(false);
  };

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <section data-testid="island-lead-section" className={compact ? 'py-12' : 'py-24 md:py-32'}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4 flex items-center gap-2">
            <MapPin size={12} strokeWidth={1.5} /> Lead Magnet · Primary Signal
          </div>
          <h2 className="text-4xl md:text-5xl font-extralight tracking-tight leading-[1.05]">
            Find your <span className="font-display italic text-[#D4AF37]">Island Base.</span>
          </h2>
          <p className="text-sm text-[#A3A3A3] mt-6 leading-relaxed font-light">
            Tell us where you want to land. EIGOO and the Sovereign Concierge will brief you on
            available compounds in your corridor — quietly, off-market, identity-anchored.
          </p>
          <div className="mt-8 space-y-3 text-xs text-[#A3A3A3] font-light">
            <div className="flex items-start gap-3"><span className="text-[#D4AF37] mt-0.5">●</span> Concierge response within 48 hours</div>
            <div className="flex items-start gap-3"><span className="text-[#D4AF37] mt-0.5">●</span> UK · Hawaii · Nigeria corridor specialists</div>
            <div className="flex items-start gap-3"><span className="text-[#D4AF37] mt-0.5">●</span> Off-market access via EIGOO&apos;s private register</div>
            <div className="flex items-start gap-3"><span className="text-[#D4AF37] mt-0.5">●</span> No spam · Sovereign discretion guaranteed</div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {submitted ? (
            <div data-testid="island-lead-success" className="sov-border bg-[#0A0A0A] p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full border-2 border-[#D4AF37] mb-6">
                <Anchor size={28} strokeWidth={1.5} className="text-[#D4AF37]" />
              </div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">Signal Received</div>
              <h3 className="text-2xl md:text-3xl font-extralight mb-4">Welcome aboard.</h3>
              <p className="text-sm text-[#A3A3A3] font-light max-w-md mx-auto leading-relaxed">
                Your lead has been routed to EIGOO&apos;s private register. A Sovereign Concierge will
                brief you within 48 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="sov-border bg-[#0A0A0A] p-8 space-y-5" data-testid="island-lead-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full Name">
                  <input required value={form.full_name} onChange={(e) => onChange('full_name', e.target.value)}
                    className="sov-input" data-testid="lead-fullname-input" />
                </Field>
                <Field label="Email">
                  <input required type="email" value={form.email} onChange={(e) => onChange('email', e.target.value)}
                    className="sov-input" data-testid="lead-email-input" />
                </Field>
              </div>
              <Field label="Phone (optional)">
                <input value={form.phone} onChange={(e) => onChange('phone', e.target.value)} className="sov-input"
                  data-testid="lead-phone-input" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Target Island / Corridor">
                  <select value={form.target_island} onChange={(e) => onChange('target_island', e.target.value)} className="sov-input"
                    data-testid="lead-island-select">
                    {ISLANDS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </Field>
                <Field label="Budget Band">
                  <select value={form.budget_band} onChange={(e) => onChange('budget_band', e.target.value)} className="sov-input"
                    data-testid="lead-budget-select">
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Intent">
                  <select value={form.intent} onChange={(e) => onChange('intent', e.target.value)} className="sov-input">
                    {INTENTS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </Field>
                <Field label="Timeline">
                  <select value={form.timeline} onChange={(e) => onChange('timeline', e.target.value)} className="sov-input">
                    {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Brief the Admiral (optional)">
                <textarea rows={3} value={form.message} onChange={(e) => onChange('message', e.target.value)}
                  placeholder="One sentence on what you're seeking…" className="sov-input resize-none"
                  data-testid="lead-message-input" />
              </Field>
              <button
                type="submit"
                disabled={submitting}
                data-testid="lead-submit"
                className="w-full py-4 bg-[#D4AF37] text-[#050505] font-medium text-sm tracking-[0.3em] uppercase hover:bg-[#E5C158] disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2} />}
                Fire the Signal
              </button>
              <p className="text-[10px] tracking-[0.25em] uppercase text-center text-[#666]">
                Sovereign discretion · Concierge brief in 48h
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">{label}</div>
    {children}
  </label>
);
